import {
  Box,
  Button,
  Container,
  Divider,
  Grid,
  IconButton,
  makeStyles,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@material-ui/core'
import React, { useState } from 'react'
import { File } from 'react-feather'
import { useTranslation } from 'react-i18next'
import { useReactiveVar } from '@apollo/client'
import { projectVar } from '../../../apollo/reactive-vars'
import {
  HourSeriesCollectionType,
  HourSeriesDataInput,
  type BuildingEnergyModelFragment,
} from '../../../generated/graphql'
import { formatArea } from '../../../util/formatters'
import { BuildingEnergyModelOptions } from './BuildingEnergyModelOptions'
import { calculateHourSeriesWithFactors } from '../../../energy-system/hour-series-calculator'
import { createHourSeriesExport } from '../../../building-energy-model/create-hourseries-export'
import { useHourSeriesCollections } from '../../../hour-series-collection/use-hour-series-collections'
import { useAuthorization } from '../../../authorization/use-authorization'
import { theme } from '../../../theme'
import { LoadingDialog } from '../../common/LoadingDialog'
import { BemCalculationTab } from './BemCalculationTab'
import { BemSummaryTab } from './BemSummaryTab'
import { useNotifications } from '../../../notifications/use-notifications'

const useStyles = makeStyles((themeSpec) => ({
  title: {
    marginTop: themeSpec.spacing(4),
    marginBottom: themeSpec.spacing(4),
  },
  subtitle1: {
    color: themeSpec.palette.grey[500],
    textTransform: 'none',
  },
  subtitleValue: {
    color: themeSpec.palette.primary.main,
    textTransform: 'none',
  },
  skipUppercase: {
    textTransform: 'none',
  },
}))

type Props = {
  model: BuildingEnergyModelFragment
  onClose: () => void
}

export function BuildingEnergyModelDrawerContent({
  model,
  onClose,
}: Props): React.ReactElement {
  const [tab, setTab] = useState(0)

  const classes = useStyles()
  const { t } = useTranslation()

  const project = useReactiveVar(projectVar)

  const canEditProject = useAuthorization().canEditProject(project)

  const { enqueueSuccess, enqueueError } = useNotifications()

  const canCalculateHourSeries =
    !!canEditProject &&
    project?.temperatureHourSeries !== null &&
    project?.calendarYear !== null &&
    model.energySystems.length > 0

  const canCalculateHourSeriesTooltip = () => {
    if (canCalculateHourSeries) return ''

    if (!canEditProject)
      return t('buildingEnergyModels.noEditProjectRightsTooptip')

    if (project?.temperatureHourSeries === null)
      return t('buildingEnergyModels.noCalcHourSeriesOutdoorTempTooltip')

    return t('buildingEnergyModels.noCalcHourSeriesNoEsystemsTooltip')
  }

  const { createHourSeriesCollection, isLoading: isHourSeriesMutationLoading } =
    useHourSeriesCollections()

  const handleExport = async () => {
    if (model.energySystems.length > 0) {
      const result = calculateHourSeriesWithFactors(
        model,
        project?.temperatureHourSeries?.values ?? [],
        project?.calendarYear ?? 0,
      )

      const workbook = createHourSeriesExport(result)

      const buffer = await workbook.xlsx.writeBuffer()

      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      })

      const fileName = `${model.name}-${new Date().toISOString()}-export.xlsx`
      saveAs(blob, fileName)
    }

    return ''
  }

  const exportHourSeriesToProject = async () => {
    if (
      project &&
      project?.temperatureHourSeries &&
      project.calendarYear &&
      model.energySystems.length > 0
    ) {
      try {
        const hourSeries = calculateHourSeriesWithFactors(
          model,
          project.temperatureHourSeries.values,
          project.calendarYear,
        )

        const hourSeriesData = hourSeries.map(
          (h) =>
            ({
              name: h.hourSerieName,
              type: h.hourSerieType,
              values: h.hourSerie.map(Math.round),
            } as HourSeriesDataInput),
        )

        await createHourSeriesCollection(
          project,
          {
            name: `${model.name}`,
            type: HourSeriesCollectionType.ProjectSpecific,
            hourSeries: hourSeriesData,
          },
          true,
        )

        enqueueSuccess(
          t('buildingEnergyModels.moveToCalculationSuccess', {
            name: model.name,
            calculationYear: project.calendarYear,
          }),
        )
      } catch {
        enqueueError(t('buildingEnergyModels.moveToCalculationError'))
      }
    }
  }

  return (
    <Box flexGrow={1} style={{ margin: theme.spacing(2) }}>
      <Container maxWidth={false}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Grid
            container
            direction="row"
            justifyContent="flex-start"
            alignItems="center"
            wrap="nowrap"
          >
            <Grid item>
              <Typography className={classes.title} variant="h3">
                {model.name}
              </Typography>
            </Grid>
          </Grid>

          <Grid
            container
            direction="row"
            justifyContent="flex-end"
            alignItems="center"
            wrap="nowrap"
          >
            <Grid item style={{ paddingRight: theme.spacing(2) }}>
              <Tooltip title={canCalculateHourSeriesTooltip()}>
                <span>
                  <Button
                    variant="contained"
                    color="default"
                    onClick={exportHourSeriesToProject}
                    disabled={!canCalculateHourSeries}
                  >
                    {t('buildingEnergyModels.moveToCalculation')}
                  </Button>
                </span>
              </Tooltip>
            </Grid>
          </Grid>

          <BuildingEnergyModelOptions
            buildingEnergyModel={model}
            onClose={onClose}
          />
        </Box>

        <Grid container direction="row" alignItems="center" spacing={0}>
          <Grid item>
            <Typography variant="subtitle1" className={classes.subtitle1}>
              {t('building.tabs.common.buildingType')}
            </Typography>
          </Grid>
          <Grid item style={{ marginLeft: theme.spacing(1) }}>
            <Typography variant="subtitle1" className={classes.subtitleValue}>
              {t(`buildingType.${model.type}`)}
            </Typography>
          </Grid>
          <Grid item style={{ marginLeft: theme.spacing(2) }}>
            <Typography variant="subtitle1" className={classes.subtitle1}>
              {t('unitName.area')}
            </Typography>
          </Grid>
          <Grid item style={{ marginLeft: theme.spacing(1) }}>
            <Typography variant="subtitle1" className={classes.subtitleValue}>
              {formatArea(model.area)}
            </Typography>
          </Grid>
          <Grid item style={{ marginLeft: theme.spacing(2) }}>
            <Typography variant="subtitle1" className={classes.subtitle1}>
              Export to Excel
            </Typography>
          </Grid>
          <Grid item style={{ marginLeft: theme.spacing(1) }}>
            <Tooltip title={canCalculateHourSeriesTooltip()}>
              <span>
                <IconButton
                  onClick={handleExport}
                  disabled={!canCalculateHourSeries}
                  style={{ padding: 0 }}
                >
                  <File />
                </IconButton>
              </span>
            </Tooltip>
          </Grid>
        </Grid>

        <LoadingDialog isLoading={isHourSeriesMutationLoading} />

        <Box mt={4}>
          <Tabs value={tab} onChange={(_, index: number) => setTab(index)}>
            <Tab
              id="summary-tab"
              label={t('buildingEnergyModels.summary.tabName')}
            />
            <Tab
              id="calculation-tab"
              label={t('buildingEnergyModels.calculationTabName')}
            />
          </Tabs>

          <Divider />

          {tab === 0 && <BemSummaryTab buildingEnergyModel={model} />}
          {tab === 1 && <BemCalculationTab buildingEnergyModel={model} />}
        </Box>
      </Container>
    </Box>
  )
}
